/**
 * Process HTML content to add specific classes to elements
 * @param html The HTML content to process
 * @returns Processed HTML with added classes
 */
export function processHtmlContent(html: string): string {
  if (!html) return '';

  // Define tag patterns for different HTML elements
  type Pattern = {
    regex: RegExp;
    replacement: string | ((match: string, ...args: any[]) => string);
  };

  const patterns: Pattern[] = [
    // Headings
    {
      regex: /<h1([^>]*)>(.*?)<\/h1>/gi,
      replacement: '<h1$1 class="font-madefor text-2xl font-bold my-2">$2</h1>',
    },
    {
      regex: /<h2([^>]*)>(.*?)<\/h2>/gi,
      replacement: '<h2$1 class="font-madefor text-xl font-bold my-1">$2</h2>',
    },
    {
      regex: /<h3([^>]*)>(.*?)<\/h3>/gi,
      replacement: '<h3$1 class="font-madefor text-lg font-bold my-1">$2</h3>',
    },

    // Paragraphs - but not those with just <br>
    {
      regex: /<p([^>]*)>(?!\s*<br\s*\/?>[\s\n]*<\/p>)(.*?)<\/p>/gis,
      replacement: '<p$1 class="font-madefor my-1">$2</p>',
    },

    // Lists
    {
      regex: /<ul([^>]*)>(.*?)<\/ul>/gis,
      replacement: '<ul$1 class="font-madefor ml-5 my-2">$2</ul>',
    },
    {
      regex: /<ol([^>]*)>(.*?)<\/ol>/gis,
      replacement: '<ol$1 class="font-madefor ml-5 my-2">$2</ol>',
    },
    {
      regex: /<li([^>]*)>(.*?)<\/li>/gi,
      replacement: '<li$1 class="font-madefor my-1">$2</li>',
    },

    // Links
    {
      regex: /<a([^>]*)>(.*?)<\/a>/gi,
      replacement:
        '<a$1 class="font-madefor text-violet-500 hover:text-violet-600 dark:hover:text-violet-400">$2</a>',
    },

    // Quotes and code
    {
      regex: /<blockquote([^>]*)>(.*?)<\/blockquote>/gis,
      replacement:
        '<blockquote$1 class="font-madefor border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-2">$2</blockquote>',
    },
    {
      regex: /<code([^>]*)>(.*?)<\/code>/gi,
      replacement:
        '<code$1 class="font-madefor bg-gray-100 dark:bg-gray-700 px-1 rounded">$2</code>',
    },
    {
      regex: /<pre([^>]*)>(.*?)<\/pre>/gis,
      replacement:
        '<pre$1 class="font-madefor bg-gray-100 dark:bg-gray-700 p-3 rounded my-2 overflow-x-auto">$2</pre>',
    },

    // Other elements (div, span, etc.) - but not br
    {
      regex: /<([a-z][a-z0-9]*)(?!br)([^>]*)>(.*?)<\/\1>/gis,
      replacement: function (match, tag, attrs, content) {
        // Skip if it's a br tag or already has font-madefor
        if (tag === 'br' || attrs.includes('font-madefor')) {
          return match;
        }
        return `<${tag}${attrs} class="font-madefor">${content}</${tag}>`;
      },
    },
  ];

  // Replace non-breaking spaces with regular spaces to prevent text overflow
  let processedHtml = html.replace(/&nbsp;/gi, ' ');

  // First pass: handle specific elements with defined styles
  for (let i = 0; i < patterns.length - 1; i++) {
    const pattern = patterns[i];
    if (typeof pattern.replacement === 'string') {
      processedHtml = processedHtml.replace(pattern.regex, pattern.replacement);
    } else {
      processedHtml = processedHtml.replace(pattern.regex, pattern.replacement);
    }
  }

  // Second pass: handle remaining elements except <br>
  // This is more complex so we use a function-based replacement
  const lastPattern = patterns[patterns.length - 1];
  processedHtml = processedHtml.replace(
    lastPattern.regex,
    (match, tag, attrs, content) => {
      // Skip if it's a br tag or already has font-madefor
      if (tag === 'br' || (attrs && attrs.includes('font-madefor'))) {
        return match;
      }
      return `<${tag}${attrs} class="font-madefor">${content}</${tag}>`;
    }
  );

  // Special case: remove any empty paragraphs that only contain <br>
  processedHtml = processedHtml.replace(
    /<p[^>]*>\s*<br\s*\/?>[\s\n]*<\/p>/gi,
    '<br>'
  );

  return processedHtml;
}
